/**
 * Load ESM libraries from CDN without webpack bundling (CRA 3.4).
 * Pattern mirrored from Pronunciation/whisper.ts.
 */

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

type LoaderEntry = {
  promise: Promise<any> | null;
  globalKey: string;
  readyEvent: string;
  scriptId: string;
};

const loaders: { [url: string]: LoaderEntry } = {};

/**
 * Dynamic-import an ESM URL and expose the module on window[globalKey].
 */
export function loadEsmModule(
  url: string,
  globalKey: string,
  timeoutMs: number = 120000
): Promise<any> {
  const win = getWin();
  if (win[globalKey]) return Promise.resolve(win[globalKey]);

  let entry = loaders[url];
  if (!entry) {
    entry = {
      promise: null,
      globalKey,
      readyEvent: 'l123-esm-ready-' + globalKey,
      scriptId: 'l123-esm-' + globalKey,
    };
    loaders[url] = entry;
  }

  if (entry.promise) return entry.promise;

  entry.promise = new Promise((resolve, reject) => {
    const doc = win.document;
    const onReady = (ev: any) => {
      win.removeEventListener(entry.readyEvent, onReady);
      const detail = ev && ev.detail;
      if (detail && detail.ok && win[globalKey]) {
        resolve(win[globalKey]);
      } else {
        entry.promise = null;
        reject(
          new Error(
            (detail && detail.err) ||
              'Không tải được thư viện từ CDN. Kiểm tra mạng rồi thử lại.'
          )
        );
      }
    };
    win.addEventListener(entry.readyEvent, onReady);

    const existing = doc.getElementById(entry.scriptId);
    if (existing) {
      setTimeout(() => {
        if (win[globalKey]) {
          win.removeEventListener(entry.readyEvent, onReady);
          resolve(win[globalKey]);
        }
      }, 0);
      return;
    }

    const s = doc.createElement('script');
    s.id = entry.scriptId;
    s.type = 'module';
    // String concat so CRA babel does not rewrite import()
    s.textContent =
      "import('" +
      url +
      "').then(function(M){" +
      'window.' +
      globalKey +
      '=M;' +
      "window.dispatchEvent(new CustomEvent('" +
      entry.readyEvent +
      "',{detail:{ok:true}}));" +
      '}).catch(function(e){' +
      "window.dispatchEvent(new CustomEvent('" +
      entry.readyEvent +
      "',{detail:{ok:false,err:String(e&&e.message||e)}}));" +
      '});';
    doc.head.appendChild(s);

    setTimeout(() => {
      if (!win[globalKey]) {
        win.removeEventListener(entry.readyEvent, onReady);
        entry.promise = null;
        reject(new Error('Hết thời gian tải thư viện. Kiểm tra mạng rồi thử lại.'));
      }
    }, timeoutMs);
  });

  return entry.promise;
}

/** @huggingface/transformers v4 (Chatterbox, etc.) */
export const HF_TRANSFORMERS_URL = 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.7.2';
export const HF_TRANSFORMERS_KEY = '__L123_HF_TRANSFORMERS__';

/**
 * Prefer transformers.js v4 when available; fall back to a known CDN build.
 * Note: Chatterbox needs v4 — if CDN pin fails, try unpkg @huggingface/transformers@4.
 */
export async function loadTransformers(): Promise<any> {
  const candidates = [
    'https://cdn.jsdelivr.net/npm/@huggingface/transformers@4.0.0',
    'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.7.2',
    'https://unpkg.com/@huggingface/transformers@4.0.0',
  ];
  let lastErr: any = null;
  for (let i = 0; i < candidates.length; i++) {
    try {
      const key = HF_TRANSFORMERS_KEY + '_' + i;
      const mod = await loadEsmModule(candidates[i], key, 180000);
      if (mod) {
        const win = getWin();
        win[HF_TRANSFORMERS_KEY] = mod;
        return mod;
      }
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Không tải được @huggingface/transformers.');
}

export async function loadKokoroJs(): Promise<any> {
  return loadEsmModule(
    'https://cdn.jsdelivr.net/npm/kokoro-js@1.2.1/+esm',
    '__L123_KOKORO_JS__',
    120000
  );
}

export async function loadOnnxRuntimeWeb(): Promise<any> {
  // ort web for Piper
  return loadEsmModule(
    'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/ort.webgpu.min.mjs',
    '__L123_ORT_WEBGPU__',
    120000
  ).catch(() =>
    loadEsmModule(
      'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/ort.wasm.min.mjs',
      '__L123_ORT_WASM__',
      120000
    )
  );
}
